/**
 * =====================================================================
 * PDF TEMPLATE MANAGEMENT - Admin Dashboard Component
 * Version: V4.52
 * Purpose: Manage PDF templates with grid view, toggles, and editing
 * =====================================================================
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as PreviewIcon,
    BarChart as AnalyticsIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import TemplateEditModal from './TemplateEditModal';
import TemplatePreviewModal from './TemplatePreviewModal';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';

interface Template {
    id: string;
    template_key: string;
    template_type: 'chapter' | 'book';
    display_name: string;
    display_description: string;
    preview_image_url: string | null;
    is_active: boolean;
    sort_order: number;
    is_premium: boolean;
    created_at: string;
    updated_at: string;
    html_length: number;
    css_length: number;
    usage_count: number;
    preference_count: number;
}

const PDFTemplateManagement: React.FC = () => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [typeFilter, setTypeFilter] = useState<'all' | 'chapter' | 'book'>('all');
    const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Modals
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);

    // =====================================================================
    // DATA FETCHING
    // =====================================================================

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build query string
            const params = new URLSearchParams();
            if (typeFilter !== 'all') params.append('type', typeFilter);
            if (activeFilter !== 'all') params.append('active', String(activeFilter === 'active'));
            if (searchQuery) params.append('search', searchQuery);

            const queryString = params.toString() ? `?${params.toString()}` : '';
            const response = await apiService.get(`/api/admin/templates${queryString}`);

            if (response.data.success) {
                setTemplates(response.data.templates);
            } else {
                setError('Failed to fetch templates');
            }
        } catch (err: any) {
            console.error('Error fetching templates:', err);
            setError(err.message || 'Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, [typeFilter, activeFilter, searchQuery]);

    // =====================================================================
    // TOGGLE ACTIVE/INACTIVE
    // =====================================================================

    const handleToggleActive = async (template: Template) => {
        try {
            const response = await apiService.patch(`/api/admin/templates/${template.id}/toggle`, {});

            if (response.data.success) {
                // Update local state
                setTemplates(templates.map(t =>
                    t.id === template.id
                        ? { ...t, is_active: response.data.template.is_active }
                        : t
                ));
            }
        } catch (err: any) {
            console.error('Error toggling template:', err);
            alert(`Failed to toggle template: ${err.message || 'Unknown error'}`);
        }
    };

    // =====================================================================
    // EDIT TEMPLATE
    // =====================================================================

    const handleEdit = (template: Template) => {
        setSelectedTemplate(template);
        setIsCreatingNew(false);
        setEditModalOpen(true);
    };

    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setIsCreatingNew(true);
        setEditModalOpen(true);
    };

    const handleEditSave = () => {
        setEditModalOpen(false);
        fetchTemplates(); // Refresh list
    };

    // =====================================================================
    // PREVIEW TEMPLATE
    // =====================================================================

    const handlePreview = (template: Template) => {
        setSelectedTemplate(template);
        setPreviewModalOpen(true);
    };

    // =====================================================================
    // DELETE TEMPLATE
    // =====================================================================

    const handleDeleteClick = (template: Template) => {
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!templateToDelete) return;

        try {
            const response = await apiService.delete(`/api/admin/templates/${templateToDelete.id}`);

            if (response.data.success) {
                setTemplates(templates.filter(t => t.id !== templateToDelete.id));
                setDeleteDialogOpen(false);
                setTemplateToDelete(null);
            }
        } catch (err: any) {
            console.error('Error deleting template:', err);
            alert(`Failed to delete template: ${err.message || 'Unknown error'}`);
        }
    };

    // =====================================================================
    // RENDER
    // =====================================================================

    return (
        <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: spacing[8], display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 700,
                            mb: spacing[2]
                        }}
                    >
                        PDF Templates
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
                        Manage PDF templates for chapters and books. Users can select these in their settings
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: spacing[2] }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchTemplates}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        Create New Template
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={typeFilter}
                                    label="Type"
                                    onChange={(e) => setTypeFilter(e.target.value as any)}
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    <MenuItem value="chapter">Chapter Templates</MenuItem>
                                    <MenuItem value="book">Book Templates</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={activeFilter}
                                    label="Status"
                                    onChange={(e) => setActiveFilter(e.target.value as any)}
                                >
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="active">Active Only</MenuItem>
                                    <MenuItem value="inactive">Inactive Only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                placeholder="Search by name or description..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Error State */}
            {error && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography>Loading templates...</Typography>
                </Box>
            )}

            {/* Templates Table */}
            {!loading && !error && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.dark' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Premium</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Usage</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Size</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            No templates found. Create your first template!
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                templates.map((template) => (
                                    <TableRow key={template.id} hover>
                                        <TableCell>
                                            <Tooltip title={template.is_active ? 'Deactivate' : 'Activate'}>
                                                <Switch
                                                    checked={template.is_active}
                                                    onChange={() => handleToggleActive(template)}
                                                    color="primary"
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {template.display_name}
                                                </Typography>
                                                {template.display_description && (
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {template.display_description.substring(0, 60)}...
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                status={template.template_type === 'chapter' ? 'success' : 'warning'}
                                                label={template.template_type}
                                                variant="filled"
                                                size="sm"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {template.template_key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                status={template.is_premium ? 'warning' : 'success'}
                                                label={template.is_premium ? 'Premium' : 'Free'}
                                                variant={template.is_premium ? 'filled' : 'outlined'}
                                                size="sm"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {template.usage_count} PDFs
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {template.preference_count} users
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {Math.round((template.html_length + template.css_length) / 1024)} KB
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEdit(template)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Preview">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => handlePreview(template)}
                                                    >
                                                        <PreviewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(template)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Summary */}
            {!loading && templates.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {templates.length} template{templates.length !== 1 ? 's' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Active: {templates.filter(t => t.is_active).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inactive: {templates.filter(t => !t.is_active).length}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Edit Modal */}
            {editModalOpen && (
                <TemplateEditModal
                    open={editModalOpen}
                    template={selectedTemplate}
                    isCreatingNew={isCreatingNew}
                    onClose={() => setEditModalOpen(false)}
                    onSave={handleEditSave}
                />
            )}

            {/* Preview Modal */}
            {previewModalOpen && selectedTemplate && (
                <TemplatePreviewModal
                    open={previewModalOpen}
                    template={selectedTemplate}
                    onClose={() => setPreviewModalOpen(false)}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Template Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to deactivate the template "{templateToDelete?.display_name}"?
                        {templateToDelete && templateToDelete.preference_count > 0 && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="body2" color="warning.dark">
                                    ⚠️ This template is currently used by {templateToDelete.preference_count} user(s).
                                    Deactivating it will revert them to the system default.
                                </Typography>
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Deactivate Template
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PDFTemplateManagement;
